import React from "react";
import { useTranslation } from "react-i18next";

import { I18N_NAMESPACES } from "../../../helpers/constants";
import { getCashAmountLabel } from "../../../helpers/i18n";
import {
  BuildYourOwnProgress as Progress,
  getBuildYourOwnProgressRatio,
  hasExtraWines,
} from "../../../helpers/buildYourOwn";
import { Bar, Detail, Fill, Headline, SpendCount } from "./style";

interface BuildYourOwnProgressMeterProps {
  progress: Progress;
  /**
   * Shop banner: celebrate the box once it is unlocked. Off inside a cart line,
   * where the redeemed box is its own item.
   */
  celebrate?: boolean;
}

/**
 * "X of N wines", the bar, and what to do next: add wines (and value, when the
 * code has a minimum total), swap wines when all are in but the total is short,
 * check out once the calendar is earned — or, for a cart holding more wines
 * than the one calendar an order can have, remove the extra ones. Before the
 * box is earned the copy is the site's own; the checkout gate still shows the
 * backend's message next to the pay button.
 */
const BuildYourOwnProgressMeter = ({ progress, celebrate = false }: BuildYourOwnProgressMeterProps) => {
  const { t } = useTranslation([I18N_NAMESPACES.COMMON]);
  const {
    freebieName,
    perNumberOfWines,
    grantedQuantity,
    winesTowardsNextUnit,
    winesUntilNextUnit,
    winesOverLastUnit,
    perWineSpend,
    wineSpendTowardsNextUnit,
    wineSpendUntilNextUnit,
  } = progress;
  const isUnlocked = grantedQuantity > 0;
  /* Wines in the calendar: all of them once it is earned. */
  const winesIn = isUnlocked ? perNumberOfWines : winesTowardsNextUnit;
  /* A code with a minimum total per box shows spend progress alongside the
   * wine count, and asks for what's still to be spent until the minimum is met. */
  const hasMinimumTotal = (perWineSpend ?? 0) > 0;
  const amount = hasMinimumTotal ? getCashAmountLabel(perWineSpend as number) : "";
  const spendRemaining = wineSpendUntilNextUnit ?? 0;
  const needsSpend = hasMinimumTotal && spendRemaining > 0;
  const remaining = needsSpend ? getCashAmountLabel(spendRemaining) : "";
  /* All the wines are in but what they come to is under that minimum. */
  const isTotalShort = needsSpend && winesUntilNextUnit === 0;

  const renderDetail = () => {
    if (hasExtraWines(progress)) {
      return t("common:buildYourOwn.tooManyWines", { count: winesOverLastUnit, perNumberOfWines });
    }
    if (isUnlocked) return t("common:buildYourOwn.complete", { perNumberOfWines });
    if (isTotalShort) {
      return t("common:buildYourOwn.spendShortToUnlock", { remaining, amount, freebieName });
    }
    return needsSpend
      ? t("common:buildYourOwn.addMoreToUnlockWithSpend", {
          count: winesUntilNextUnit,
          remaining,
          freebieName,
        })
      : t("common:buildYourOwn.addMoreToUnlock", { count: winesUntilNextUnit, freebieName });
  };

  return (
    <>
      <Headline>
        {celebrate && isUnlocked
          ? t("common:buildYourOwn.unlocked", { count: grantedQuantity, freebieName })
          : t("common:buildYourOwn.winesCount", { count: winesIn, perNumberOfWines })}
      </Headline>
      <Bar
        role="progressbar"
        aria-label={t("common:buildYourOwn.progressLabel")}
        aria-valuemin={0}
        aria-valuemax={perNumberOfWines}
        aria-valuenow={winesIn}
      >
        <Fill $ratio={getBuildYourOwnProgressRatio(progress)} />
      </Bar>
      {hasMinimumTotal && !isUnlocked && (
        <SpendCount data-testid="build-your-own-spend-count">
          {t("common:buildYourOwn.spendCount", {
            spent: getCashAmountLabel(wineSpendTowardsNextUnit ?? 0),
            amount,
          })}
        </SpendCount>
      )}
      <Detail>{renderDetail()}</Detail>
    </>
  );
};

export default BuildYourOwnProgressMeter;
